import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, redirect, RouterProvider } from 'react-router';
import { MotionConfig } from 'framer-motion';

import { ThemeProvider } from './app/theme.jsx';

// i18next must boot before any component renders so first paint is in
// the right language and react-i18next's <I18nextProvider> isn't
// strictly needed (the singleton i18n instance suffices).
import './i18n/index.js';
import './tailwind.css';

import Root from './root.jsx';
import IndexRoute from './routes/_index.jsx';
import LoginRoute from './routes/login.jsx';
import { projectViewLoader } from './routes/project-view.loader.js';
import { connectionsRedirectLoader } from './routes/connections-redirect.js';
import { consumptionRedirectTarget } from './lib/audit-url.js';

// PWA service worker registration temporarily disabled — see TODO in
// vite.config.js for the vite-plugin-pwa + React Router v7 incompat.
// Re-enable when the upstream fix lands:
//
//   import { registerSW } from 'virtual:pwa-register';
//   registerSW({ immediate: true });

/** Code-split route module → react-router `lazy` entry. */
const lazyDefault = (load) => async () => ({ Component: (await load()).default });

// Single source of truth for the route tree (React Router DATA mode —
// the framework plugin and its routes.js duplicate were removed 2026-06-12).
const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    children: [
      { index: true, element: <IndexRoute /> },
      { path: 'login', element: <LoginRoute /> },
      { path: 'start', lazy: lazyDefault(() => import('./routes/start.jsx')) },
      { path: 'playground', lazy: lazyDefault(() => import('./routes/playground.jsx')) },
      { path: 'lab/fil', lazy: lazyDefault(() => import('./routes/lab.fil.jsx')) },
      { path: 'lab2', lazy: lazyDefault(() => import('./routes/lab2.jsx')) },
      { path: 'lab3', lazy: lazyDefault(() => import('./routes/lab3.jsx')) },
      { path: 'lab4', lazy: lazyDefault(() => import('./routes/lab4.jsx')) },
      { path: 'lab5', lazy: lazyDefault(() => import('./routes/lab5.jsx')) },
      { path: 'lab6', lazy: lazyDefault(() => import('./routes/lab6.jsx')) },
      { path: 'lab7', lazy: lazyDefault(() => import('./routes/lab7.jsx')) },
      { path: 'lab8', lazy: lazyDefault(() => import('./routes/lab8.jsx')) },
      { path: 'lab9', lazy: lazyDefault(() => import('./routes/lab9.jsx')) },
      { path: 'lab10', lazy: lazyDefault(() => import('./routes/lab10.jsx')) },
      { path: 'champions/:slug', lazy: lazyDefault(() => import('./routes/champions.$slug.jsx')) },
      { path: 'onboarding', lazy: lazyDefault(() => import('./routes/onboarding.jsx')) },
      { path: 'invitations', lazy: lazyDefault(() => import('./routes/invitations.jsx')) },
      {
        path: 'offices/:officeId',
        lazy: lazyDefault(() => import('./routes/office-layout.jsx')),
        children: [
          { path: 'home', lazy: lazyDefault(() => import('./routes/home.jsx')) },
          { path: 'dashboard', loader: ({ params }) => redirect(`/offices/${params.officeId}/home`) },
          { path: 'missions', lazy: lazyDefault(() => import('./routes/missions.jsx')) },
          { path: 'routines', lazy: lazyDefault(() => import('./routes/routines.jsx')) },
          { path: 'automations', lazy: lazyDefault(() => import('./routes/automations.jsx')) },
          { path: 'projects', lazy: lazyDefault(() => import('./routes/projects._index.jsx')) },
          // Stream B (M3) — flattened to a single leaf route. The shell
          // (projects.$projectId.jsx → ProjectViewShell) owns the tab model and
          // renders all panels IN-PLACE via ?tab= (spec §2.1). The former nested
          // children (index→board, missions, wiki) are preserved as redirect
          // stubs so deep links keep working : /projects/:id/missions →
          // ?tab=missions, /wiki → ?tab=wiki (Story 8 : unchanged behavior —
          // they render the same components, now as in-place panels).
          {
            path: 'projects/:projectId',
            // loader runs in PARALLEL with the lazy module download and warms
            // the TanStack cache (render-as-you-fetch) — kills the project
            // view's render-then-fetch waterfall. See project-view.loader.js.
            loader: projectViewLoader,
            lazy: lazyDefault(() => import('./routes/projects.$projectId.jsx')),
          },
          {
            path: 'projects/:projectId/missions',
            loader: ({ params }) =>
              redirect(`/offices/${params.officeId}/projects/${params.projectId}?tab=missions`),
          },
          {
            path: 'projects/:projectId/wiki',
            loader: ({ params }) =>
              redirect(`/offices/${params.officeId}/projects/${params.projectId}?tab=wiki`),
          },
          { path: 'groups', lazy: lazyDefault(() => import('./routes/products._index.jsx')) },
          {
            path: 'groups/:productId/settings',
            lazy: lazyDefault(() => import('./routes/products.$productId.settings.jsx')),
          },
          {
            path: 'groups/:productId',
            lazy: lazyDefault(() => import('./routes/products.$productId._index.jsx')),
          },
          // Back-compat : the page was /products before the "groups" rename.
          { path: 'products', loader: ({ params }) => redirect(`/offices/${params.officeId}/groups`) },
          { path: 'products/:productId', loader: ({ params }) => redirect(`/offices/${params.officeId}/groups/${params.productId}`) },
          { path: 'missions/:missionId', lazy: lazyDefault(() => import('./routes/missions.$missionId.jsx')) },
          {
            path: 'missions/:missionId/flow',
            lazy: lazyDefault(() => import('./routes/missions.$missionId.flow.jsx')),
          },
          {
            path: 'missions/:missionId/champion',
            lazy: lazyDefault(() => import('./routes/missions.$missionId.champion.jsx')),
          },
          { path: 'agents/:agentId', lazy: lazyDefault(() => import('./routes/agents.$agentId.jsx')) },
          { path: 'connectors', lazy: lazyDefault(() => import('./routes/connectors.jsx')) },
          { path: 'processes', lazy: lazyDefault(() => import('./routes/processes.jsx')) },
          // connections-page S-001 : data sources + tooling on one office page.
          { path: 'connections', lazy: lazyDefault(() => import('./routes/office-connections.jsx')) },
          { path: 'org', lazy: lazyDefault(() => import('./routes/org-chart.jsx')) },
          // Agency-level Worklenz views (Wave 1 scaffold — mock-first). The 4
          // page roots render standalone; 4 parallel streams fill them in.
          { path: 'reporting', lazy: lazyDefault(() => import('./routes/reporting.jsx')) },
          { path: 'reporting/:section', lazy: lazyDefault(() => import('./routes/reporting.jsx')) },
          { path: 'schedule', lazy: lazyDefault(() => import('./routes/schedule.jsx')) },
          { path: 'admin', lazy: lazyDefault(() => import('./routes/admin-center.jsx')) },
          { path: 'account-settings', lazy: lazyDefault(() => import('./routes/account-settings.jsx')) },
          { path: 'skills', lazy: lazyDefault(() => import('./routes/skills.jsx')) },
          { path: 'guild', lazy: lazyDefault(() => import('./routes/guild.jsx')) },
          { path: 'billing', lazy: lazyDefault(() => import('./routes/billing.jsx')) },
          { path: 'wiki/:pageSlug?', lazy: lazyDefault(() => import('./routes/office-wiki.jsx')) },
          { path: 'settings', lazy: lazyDefault(() => import('./routes/office-settings.jsx')) },
          { path: 'settings/models', lazy: lazyDefault(() => import('./routes/office-models.jsx')) },
          // agent-spend-audit S-007 : Consommation is replaced by the Audit agents page ; the old URL redirects with
          // its query string verbatim (from / to / mission, the page drops what it does not know).
          { path: 'audit', lazy: lazyDefault(() => import('./routes/office-audit.jsx')) },
          {
            path: 'settings/consumption',
            loader: ({ params, request }) => redirect(consumptionRedirectTarget(params.officeId, request.url)),
          },
          // Back-compat (connections-page D-23) : the page moved to /connections.
          // Composio return links (?data_source=) and mission deep links keep
          // working — only the three named params are carried, no open
          // pass-through of the query string.
          { path: 'settings/data-sources', loader: connectionsRedirectLoader },
          // GitHub repo onboarding (mission gwt-g1-repo-onboarding, spec §4) —
          // additive, new paths under settings/github/*.
          { path: 'settings/github/connect', lazy: lazyDefault(() => import('./routes/office-github-connect.jsx')) },
          { path: 'settings/github/guide', lazy: lazyDefault(() => import('./routes/office-github-guide.jsx')) },
        ],
      },
    ],
  },
]);

// reducedMotion="user" makes every framer-motion animation in the tree honor
// the OS "reduce motion" setting (transform/layout motion is dropped, opacity
// still cross-fades) — WCAG 2.3.3. CSS transitions are handled separately in
// tailwind.css's prefers-reduced-motion block.
createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ThemeProvider>
      <MotionConfig reducedMotion="user">
        <RouterProvider router={router} />
      </MotionConfig>
    </ThemeProvider>
  </StrictMode>,
);
